/**
 * `BiDatePicker` — drop-in dark + orange replacement for `<input type="date">`.
 *
 * Same API as the native input:
 *   <BiDatePicker
 *     value="2026-02-15"                              // ISO YYYY-MM-DD (or "")
 *     onChange={(e) => setForm({...form, d: e.target.value})}
 *     className="w-full bi-input"
 *     data-testid="rv-date"
 *   />
 *
 * Trigger shows the date in Australian format (DD/MM/YYYY) — matching the rest
 * of the app — but `onChange` emits ISO `YYYY-MM-DD` so persisted values are
 * unchanged. Popover uses the shadcn Calendar (react-day-picker) with orange
 * selected/today accents and a dark #1F1F1F surface. Matches the JobPickerSheet
 * + BiSelect visual language.
 */
import { useState, useMemo } from "react";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

/** Parse ISO `YYYY-MM-DD` → local Date at midnight (avoiding UTC drift). */
function parseIso(s) {
  if (!s || typeof s !== "string") return undefined;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Date → ISO `YYYY-MM-DD` in the LOCAL calendar (matches native `<input type=date>`). */
function toIso(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Date → DD/MM/YYYY (en-AU) for display in the trigger. */
function formatAu(d) {
  if (!d) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const y = d.getFullYear();
  return `${day}/${m}/${y}`;
}

export default function BiDatePicker({
  value,
  onChange,
  className,
  disabled,
  placeholder = "DD/MM/YYYY",
  min,
  max,
  "data-testid": testId,
  "aria-label": ariaLabel,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseIso(value), [value]);
  const minDate = useMemo(() => parseIso(min), [min]);
  const maxDate = useMemo(() => parseIso(max), [max]);

  const handlePick = (d) => {
    if (!d) return;
    setOpen(false);
    onChange?.({ target: { value: toIso(d) } });
  };

  const disabledMatcher =
    minDate || maxDate
      ? (day) => (minDate && day < minDate) || (maxDate && day > maxDate)
      : undefined;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            "bi-input inline-flex items-center justify-between gap-2 text-left disabled:opacity-60 disabled:cursor-not-allowed",
            className,
          )}
          {...rest}
        >
          <span
            className={cn(
              "truncate flex-1 min-w-0",
              !selected && "text-white/40",
            )}
          >
            {selected ? formatAu(selected) : placeholder}
          </span>
          <CalendarIcon className="w-4 h-4 text-white/60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 bg-[#1F1F1F] border border-white/10 text-white rounded-xl shadow-[0_20px_50px_-10px_rgba(0,0,0,0.7)] w-auto"
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handlePick}
          defaultMonth={selected}
          disabled={disabledMatcher}
          initialFocus
          className="text-white"
          classNames={{
            caption_label: "text-sm font-heading font-bold text-white",
            nav_button:
              "h-7 w-7 bg-transparent p-0 rounded-md text-white/70 hover:bg-white/10 hover:text-white transition inline-flex items-center justify-center",
            nav_button_previous: "absolute left-2",
            nav_button_next: "absolute right-2",
            head_cell:
              "text-white/40 rounded-md w-9 font-heading text-[10px] uppercase tracking-wider",
            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
            day: "h-9 w-9 p-0 font-medium text-white/85 rounded-md hover:bg-white/[0.08] hover:text-white transition inline-flex items-center justify-center",
            day_selected:
              "!bg-[#FF9500] !text-[#1F1F1F] font-heading font-extrabold hover:!bg-[#FF9500] hover:!text-[#1F1F1F] focus:!bg-[#FF9500] shadow-[0_6px_18px_-6px_rgba(255,149,0,0.7)]",
            day_today:
              "border border-[#FF9500]/50 text-[#FF9500] font-heading font-bold",
            day_outside: "text-white/25",
            day_disabled: "text-white/20 opacity-50",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
